import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Button } from '@components/atoms/Button';
import PaymentMethodSelector from '@pages/register/components/PaymentMethodSelector';
import {
  buildPaymentMethodValidationSchema,
  paymentMethodInitialValues,
} from '@pages/register/components/PaymentMethodSelector/validationSchema';
import { useAppSelector } from '@redux/hooks';
import { PaymentMethodType } from '@models/subscription';
import { useTranslation } from '@hooks/useTranslation';

interface PaymentMethodFormProps {
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  onSubmit,
  onCancel,
}) => {
  const tPaymentMethod = useTranslation('paymentMethod');
  const tBilling = useTranslation('billing');
  const tCommon = useTranslation('common');
  const validationSchema = useMemo(
    () => buildPaymentMethodValidationSchema(tPaymentMethod),
    [tPaymentMethod],
  );
  const user = useAppSelector((state) => state.user);
  const organization = useAppSelector((state) => state.organization);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: paymentMethodInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitError(null);

      const paymentData: any = {
        organizationId: organization.organizationId,
        paymentMethodType: values.paymentMethodType,
        firstName: values.billingFirstName,
        lastName: values.billingLastName,
        email: values.billingEmail,
        address1: values.billingAddress1,
        address2: values.billingAddress2,
        city: values.billingCity,
        state: values.billingState,
        zip: values.billingZip,
        country: values.billingCountry,
        phone: values.billingPhone,
      };

      // Add payment-method-specific fields
      if (values.paymentMethodType === PaymentMethodType.CREDIT_CARD) {
        const ccnumber = values.ccNumber.replace(/\s/g, '');
        const ccexp = values.ccExp.replace(/\//g, '');
        paymentData.ccNumber = ccnumber;
        paymentData.ccExp = ccexp;
      }
      try {
        await onSubmit(paymentData);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : tBilling.paymentMethods.addFailedFallback;
        setSubmitError(errorMessage);
        console.error('Payment method submission failed:', error);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-6">
      {submitError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400">
            {submitError}
          </p>
        </div>
      )}

      <PaymentMethodSelector
        formik={formik}
        userData={{
          firstName: user.firstName || undefined,
          lastName: user.lastName || undefined,
          email: user.email || undefined,
        }}
        showBillingCycle={false}
        showBillingPeriod={false}
        showPlanSelection={false}
      />

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
        <Button type="button" variant="secondary" onClick={onCancel}>
          {tCommon.cancel}
        </Button>
        <Button
          id="payment-submit-button"
          type="submit"
          variant="primary"
          disabled={formik.isSubmitting}
          loading={formik.isSubmitting}>
          {tBilling.paymentMethods.addButton}
        </Button>
      </div>
    </form>
  );
};

export default PaymentMethodForm;
